import QRCode from 'qrcode'

/**
 * Generates multiple QR codes in parallel batches for better performance
 * @param {Array} values - Array of strings to encode as QR codes
 * @param {Object} options - QR code generation options
 * @param {Function} onProgress - Callback for progress updates (current, total)
 * @returns {Promise<Object>} Map of value -> dataUrl
 */
export async function generateQRCodesBatch(values, options = {}, onProgress = null) {
  const {
    errorCorrectionLevel = 'M',
    width = 200,
    margin = 1,
    batchSize = 100, // Generate 100 at a time
  } = options

  const results = {}
  const total = values.length

  for (let i = 0; i < values.length; i += batchSize) {
    const batch = values.slice(i, i + batchSize)

    // Generate this batch in parallel
    const batchPromises = batch.map((value) =>
      QRCode.toDataURL(value, {
        errorCorrectionLevel,
        type: 'image/png',
        width,
        margin,
      })
    )

    try {
      const batchResults = await Promise.all(batchPromises)

      // Map results to values
      batch.forEach((value, index) => {
        results[value] = batchResults[index]
      })

      // Report progress
      if (onProgress) {
        const completed = Math.min(i + batchSize, total)
        onProgress(completed, total)
      }

      // Small delay to prevent blocking (only between batches, not per item)
      if (i + batchSize < values.length) {
        await new Promise((resolve) => setTimeout(resolve, 10))
      }
    } catch (error) {
      console.error('Batch QR generation error:', error)
      // Continue with other batches even if one fails
    }
  }

  return results
}
